import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import AlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';

import { useDebounce } from 'src/hooks/use-debounce';

import { ICD_ERROR } from 'src/services/ICD/icd-client';
import { esCodigoCie, valorParaFormulario } from 'src/services/ICD/icd-normalize';
import { obtenerRecientes, registrarReciente } from 'src/services/ICD/icd-recientes';
import { buscarPorCodigo, buscarDiagnosticos } from 'src/services/ICD/icd-search-service';

import Iconify from 'src/components/iconify';
import { IcdOptionRow, IcdAdvancedDialog } from 'src/components/icd-search';

// ----------------------------------------------------------------------
// Campo de diagnóstico CIE-11 contra el servicio ICD-API propio.
//
// El dato persistido sigue siendo el string histórico "CÓDIGO - TÍTULO", pero
// el campo ya no acepta texto tecleado: lo escrito es solo búsqueda y el valor
// únicamente se fija seleccionando del catálogo.
// ----------------------------------------------------------------------

const MINIMO_CARACTERES = 3;

const RETARDO_BUSQUEDA = 250;

const ALTO_LISTA = 384;

const FUENTE_MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const GRUPO = Object.freeze({
  RECIENTES: 'recientes',
  EXACTO: 'exacto',
  RESULTADOS: 'resultados',
});

const ETIQUETAS_GRUPO = Object.freeze({
  [GRUPO.RECIENTES]: 'Diagnósticos recientes',
  [GRUPO.EXACTO]: 'Código exacto',
  [GRUPO.RESULTADOS]: 'Resultados del catálogo',
});

const barraFina = (theme) => ({
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: alpha(theme.palette.grey[500], 0.32),
  },
});

/**
 * Un valor que ya venía en el formulario se parte en código y título solo para
 * pintarlo bonito. Nunca se resuelve contra la API: es dato histórico legítimo.
 */
function separarValorHeredado(valor) {
  const texto = String(valor || '').trim();
  if (!texto) return null;

  const corte = texto.indexOf(' - ');
  if (corte > 0) {
    const codigo = texto.slice(0, corte).trim();
    const titulo = texto.slice(corte + 3).trim();
    if (titulo && esCodigoCie(codigo)) return { id: null, codigo, titulo };
  }

  return { id: null, codigo: '', titulo: texto };
}

// ----------------------------------------------------------------------

// El mousedown se anula en todo el papel: sin esto, pulsar «Reintentar» o
// «Búsqueda avanzada» le quita el foco al input y el desplegable se cierra
// antes de que llegue el click.
function PapelDesplegable({ children, ...otros }) {
  return (
    <Paper {...otros} onMouseDown={(evento) => evento.preventDefault()}>
      {children}

      <Divider />

      <Typography
        variant="caption"
        sx={{ px: 1.5, py: 0.75, display: 'block', color: 'text.disabled' }}
      >
        ↑ ↓ recorrer · Enter usar · Esc cerrar
      </Typography>
    </Paper>
  );
}

PapelDesplegable.propTypes = {
  children: PropTypes.node,
};

// ----------------------------------------------------------------------

function CabeceraGrupo({ grupo, total }) {
  return (
    <Stack
      spacing={1}
      direction="row"
      alignItems="center"
      sx={(theme) => ({
        px: 1.5,
        py: 0.75,
        top: 0,
        zIndex: 1,
        position: 'sticky',
        bgcolor: theme.palette.background.paper,
        borderBottom: `dashed 1px ${theme.palette.divider}`,
      })}
    >
      <Typography variant="overline" sx={{ fontSize: 10, color: 'text.disabled' }}>
        {ETIQUETAS_GRUPO[grupo] ?? grupo}
      </Typography>

      <Box sx={{ flexGrow: 1 }} />

      {total > 1 && (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {total}
        </Typography>
      )}
    </Stack>
  );
}

CabeceraGrupo.propTypes = {
  total: PropTypes.number,
  grupo: PropTypes.string,
};

// ----------------------------------------------------------------------

// Un fallo del servicio jamás puede parecer «ese diagnóstico no existe».
function AvisoError({ error, onReintentar }) {
  return (
    <Alert
      severity="error"
      sx={{ textAlign: 'left' }}
      action={
        <Button
          size="small"
          color="error"
          onClick={onReintentar}
          startIcon={<Iconify width={16} icon="eva:refresh-fill" />}
        >
          Reintentar
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0.25 }}>No se pudo consultar el catálogo</AlertTitle>

      <Typography variant="caption">{error?.message}</Typography>
    </Alert>
  );
}

AvisoError.propTypes = {
  error: PropTypes.object,
  onReintentar: PropTypes.func,
};

// ----------------------------------------------------------------------

function AvisoVacio({ icono, titulo, descripcion, accion }) {
  return (
    <Stack spacing={0.75} alignItems="center" sx={{ py: 1.5, textAlign: 'center' }}>
      <Iconify width={30} icon={icono} sx={{ color: 'text.disabled' }} />

      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
        {titulo}
      </Typography>

      {!!descripcion && (
        <Typography variant="caption" sx={{ maxWidth: 380, color: 'text.disabled' }}>
          {descripcion}
        </Typography>
      )}

      {accion}
    </Stack>
  );
}

AvisoVacio.propTypes = {
  accion: PropTypes.node,
  titulo: PropTypes.string,
  icono: PropTypes.string,
  descripcion: PropTypes.string,
};

// ----------------------------------------------------------------------

export default function RHFIcdAutocomplete({
  name,
  label,
  disabled = false,
  placeholder = 'Escribe el diagnóstico, un sinónimo o el código CIE-11',
  helperText,
  onSeleccionar,
}) {
  const { control, setValue } = useFormContext();

  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(null);
  const [intento, setIntento] = useState(0);
  const [recientes, setRecientes] = useState(() => obtenerRecientes());
  const [expandida, setExpandida] = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);
  const [dialogo, setDialogo] = useState(null);

  const consulta = useDebounce(texto, RETARDO_BUSQUEDA);
  const consultaVigente = useRef('');

  const busqueda = texto.trim();
  const busquedaResuelta = consulta.trim();

  // Mientras el debounce no ha alcanzado a lo tecleado seguimos «buscando»: sin
  // esto, la tercera letra pinta un «sin coincidencias» que dura 250 ms.
  const esperando =
    cargando || (busqueda.length >= MINIMO_CARACTERES && busqueda !== busquedaResuelta);

  useEffect(() => {
    consultaVigente.current = busquedaResuelta;
    setExpandida(null);

    if (busquedaResuelta.length < MINIMO_CARACTERES) {
      setResultados([]);
      setFallo(null);
      setCargando(false);
      return undefined;
    }

    const abortador = new AbortController();

    setCargando(true);
    setFallo(null);

    (async () => {
      try {
        const lista = await buscarDiagnosticos(busquedaResuelta, { signal: abortador.signal });

        // /search no encuentra códigos: el exacto llega por /codeinfo y se ancla
        // arriba. Que falle esa consulta no puede tumbar el resto de resultados.
        let exacto = null;
        if (esCodigoCie(busquedaResuelta)) {
          exacto = await buscarPorCodigo(busquedaResuelta, { signal: abortador.signal }).catch(
            () => null
          );
        }

        // Doble guarda: el abort corta el transporte y la consulta vigente
        // descarta la respuesta lenta de «diab» que llegue después de «diabetes».
        if (abortador.signal.aborted || consultaVigente.current !== busquedaResuelta) return;

        const encontrados = lista
          .filter((item) => !exacto || item.id !== exacto.id)
          .map((item) => ({ ...item, grupo: GRUPO.RESULTADOS }));

        setResultados(exacto ? [{ ...exacto, grupo: GRUPO.EXACTO }, ...encontrados] : encontrados);
        setCargando(false);
      } catch (error) {
        if (error?.tipo === ICD_ERROR.CANCELADO) return;
        if (consultaVigente.current !== busquedaResuelta) return;

        setResultados([]);
        setFallo(error);
        setCargando(false);
      }
    })();

    return () => abortador.abort();
  }, [busquedaResuelta, intento]);

  // setValue (y no field.onChange) porque el mismo callback lo invocan el
  // desplegable, la fila expandida y el diálogo, todos fuera del render del
  // Controller, donde `field` no existe.
  const seleccionar = useCallback(
    (diagnostico) => {
      if (!diagnostico) return;

      setSeleccionado(diagnostico);
      setValue(name, valorParaFormulario(diagnostico), { shouldValidate: true, shouldDirty: true });
      registrarReciente(diagnostico);
      setRecientes(obtenerRecientes());
      setTexto('');
      setExpandida(null);
      setDialogo(null);

      // El objeto completo por las tres vías: quien necesite el id CIE no
      // depende de que localStorage haya podido guardar el reciente.
      if (onSeleccionar) onSeleccionar(diagnostico);
    },
    [name, setValue, onSeleccionar]
  );

  const limpiar = useCallback(() => {
    setSeleccionado(null);
    setValue(name, '', { shouldValidate: true, shouldDirty: true });
    setTexto('');
  }, [name, setValue]);

  const abrirCatalogo = useCallback(
    (diagnostico, consultaAlterna) => {
      setDialogo({
        id: diagnostico?.id || null,
        consulta: diagnostico?.id ? '' : (consultaAlterna ?? busqueda),
      });
    },
    [busqueda]
  );

  const opciones = useMemo(() => {
    if (!busqueda) return recientes.map((item) => ({ ...item, grupo: GRUPO.RECIENTES }));
    if (busqueda.length < MINIMO_CARACTERES) return [];
    return resultados;
  }, [busqueda, recientes, resultados]);

  const renderSinOpciones = () => {
    if (fallo) return <AvisoError error={fallo} onReintentar={() => setIntento((n) => n + 1)} />;

    if (!busqueda) {
      return (
        <AvisoVacio
          icono="eva:clock-outline"
          titulo="Aún no tienes diagnósticos recientes"
          descripcion="Escribe el nombre, un sinónimo coloquial como «presión alta» o el código CIE-11. Los que uses aparecerán aquí la próxima vez."
        />
      );
    }

    if (busqueda.length < MINIMO_CARACTERES) {
      return (
        <AvisoVacio
          icono="eva:edit-2-outline"
          titulo="Escribe al menos 3 letras"
          descripcion="El catálogo busca por palabras, así que necesita algo más de texto."
        />
      );
    }

    return (
      <AvisoVacio
        icono="eva:question-mark-circle-outline"
        titulo={`Sin coincidencias para «${busqueda}»`}
        descripcion="Prueba con otra palabra o recorre el catálogo por capítulos."
        accion={
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            sx={{ mt: 0.5 }}
            onClick={() => abrirCatalogo(null)}
            startIcon={<Iconify width={16} icon="eva:book-open-outline" />}
          >
            Búsqueda avanzada
          </Button>
        }
      />
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // La tarjeta se deriva SIEMPRE del valor del formulario: así un reset o
        // una carga de datos externa la actualizan sin tocar estado local.
        const valorActual = String(field.value || '');
        const vigente = seleccionado && valorParaFormulario(seleccionado) === valorActual;
        const tarjeta = vigente ? seleccionado : separarValorHeredado(valorActual);

        return (
          <Box sx={{ width: 1 }}>
            <Autocomplete
              fullWidth
              openOnFocus
              disabled={disabled}
              value={null}
              options={opciones}
              inputValue={texto}
              loading={esperando}
              filterOptions={(lista) => lista}
              groupBy={(opcion) => opcion.grupo}
              noOptionsText={renderSinOpciones()}
              loadingText="Buscando en el catálogo CIE-11…"
              PaperComponent={PapelDesplegable}
              getOptionLabel={(opcion) => opcion?.titulo || ''}
              isOptionEqualToValue={(opcion, valor) => opcion?.id === valor?.id}
              onOpen={() => setRecientes(obtenerRecientes())}
              onChange={(evento, valor) => seleccionar(valor)}
              onInputChange={(evento, valor, razon) => {
                // Solo lo tecleado manda: el «reset» de MUI intentaría volcar
                // aquí la etiqueta del valor y esto es una caja de búsqueda.
                if (razon === 'input') setTexto(valor);
                if (razon === 'clear') setTexto('');
              }}
              onBlur={() => {
                field.onBlur();
                setTexto('');
                setExpandida(null);
              }}
              ListboxProps={{
                sx: (theme) => ({ maxHeight: ALTO_LISTA, ...barraFina(theme) }),
              }}
              renderGroup={(params) => (
                <li key={params.key}>
                  <CabeceraGrupo grupo={params.group} total={params.children?.length} />

                  <Box component="ul" sx={{ p: 0, m: 0 }}>
                    {params.children}
                  </Box>
                </li>
              )}
              renderOption={(props, opcion) => (
                <Box
                  component="li"
                  {...props}
                  key={`${opcion.grupo}-${opcion.id || opcion.codigo}`}
                  sx={{
                    p: 0.25,
                    display: 'block',
                    '&.Mui-focused': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                >
                  <IcdOptionRow
                    consulta={busqueda}
                    diagnostico={opcion}
                    onUsar={seleccionar}
                    onAbrirCatalogo={abrirCatalogo}
                    expandida={!!opcion.id && expandida === opcion.id}
                    onAlternarDetalle={() =>
                      setExpandida((previo) => (previo === opcion.id ? null : opcion.id))
                    }
                  />
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  inputRef={field.ref}
                  placeholder={placeholder}
                  error={!!error}
                  helperText={error ? error?.message : helperText}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify
                          width={20}
                          icon="eva:search-fill"
                          sx={{ color: 'text.disabled' }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        <InputAdornment position="end" sx={{ gap: 0.25 }}>
                          {!!tarjeta && !disabled && (
                            <Tooltip title="Quitar diagnóstico" arrow>
                              <IconButton size="small" onClick={limpiar} aria-label="Quitar">
                                <Iconify width={18} icon="eva:close-circle-fill" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Catálogo completo" arrow>
                            <span>
                              <IconButton
                                size="small"
                                disabled={disabled}
                                aria-label="Catálogo completo"
                                onClick={() => abrirCatalogo(null)}
                                onMouseDown={(evento) => evento.preventDefault()}
                              >
                                <Iconify width={18} icon="eva:book-open-outline" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>

                        {/* Absoluta contra el borde del input: no desplaza nada. */}
                        {esperando && (
                          <LinearProgress
                            sx={(theme) => ({
                              left: 0,
                              right: 0,
                              bottom: 0,
                              height: 2,
                              position: 'absolute',
                              borderBottomLeftRadius: theme.shape.borderRadius,
                              borderBottomRightRadius: theme.shape.borderRadius,
                            })}
                          />
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />

            {!!tarjeta && (
              <Stack
                spacing={1}
                direction="row"
                alignItems="center"
                sx={(theme) => ({
                  mt: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  border: `solid 1px ${alpha(theme.palette.primary.main, 0.16)}`,
                })}
              >
                <Iconify
                  width={18}
                  icon="eva:checkmark-circle-2-fill"
                  sx={{ flexShrink: 0, color: 'primary.main' }}
                />

                {!!tarjeta.codigo && (
                  <Chip
                    size="small"
                    variant="soft"
                    color="primary"
                    label={tarjeta.codigo}
                    sx={{
                      flexShrink: 0,
                      fontFamily: FUENTE_MONO,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  />
                )}

                <Typography variant="caption" sx={{ flexGrow: 1, minWidth: 0 }}>
                  {tarjeta.titulo}
                </Typography>

                <Tooltip title="Ver detalle completo" arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      aria-label="Ver detalle completo"
                      onClick={() => abrirCatalogo(tarjeta, tarjeta.titulo)}
                    >
                      <Iconify width={16} icon="eva:external-link-fill" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}

            <IcdAdvancedDialog
              abierto={!!dialogo}
              idInicial={dialogo?.id || null}
              onSeleccionar={seleccionar}
              onCerrar={() => setDialogo(null)}
              consultaInicial={dialogo?.consulta || ''}
            />
          </Box>
        );
      }}
    />
  );
}

RHFIcdAutocomplete.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  helperText: PropTypes.node,
  onSeleccionar: PropTypes.func,
  placeholder: PropTypes.string,
  name: PropTypes.string.isRequired,
};
